import React, { useState, useEffect } from "react";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import BarLoader from "react-spinners/BarLoader";
import { parseGedFile, buildFamilyTree, getRootAncestors, getDescendants, getAncestors } from "../utils/gedParser";

const loader = <BarLoader height={4} width={"100vw"} color={"#00BFFF"} />;

const containerStyle = {
    padding: "1em 2em",
    minHeight: "80vh",
};

const statsCardStyle = {
    marginBottom: "1em",
    backgroundColor: "inherit",
};

const statsValueStyle = {
    fontSize: "1.8em",
    fontWeight: "bold",
    color: "#0056b3",
};

const personCardStyle = {
    marginBottom: "1em",
    cursor: "pointer",
    transition: "all 0.2s",
};

// Utility functions
const extractYear = (dateString) => {
    if (!dateString) return Infinity;
    const parts = dateString.split(" ");
    const year = parseInt(parts[parts.length - 1], 10);
    return Number.isNaN(year) ? Infinity : year;
};

const getSexDisplay = (sex) => {
    if (sex === "M") return "Male";
    if (sex === "F") return "Female";
    return "Unknown";
};

const calculateAverageAge = (people) => {
    const withDates = people.filter((p) => p.birthData?.date && p.deathData?.date);
    if (withDates.length === 0) return "N/A";

    const totalAge = withDates.reduce((sum, p) => {
        const birth = extractYear(p.birthData?.date);
        const death = extractYear(p.deathData?.date);
        return sum + (death - birth);
    }, 0);

    return (totalAge / withDates.length).toFixed(1);
};

const buildGenerations = (people) => {
    const generations = [];
    const roots = getRootAncestors(people);

    const addToGeneration = (person, generationIndex) => {
        if (!generations[generationIndex]) {
            generations[generationIndex] = [];
        }
        if (!generations[generationIndex].find((p) => p.id === person.id)) {
            generations[generationIndex].push(person);
        }

        if (person.children) {
            person.children.forEach((child) => {
                addToGeneration(child, generationIndex + 1);
            });
        }
    };

    roots.forEach((root) => {
        addToGeneration(root, 0);
    });

    return generations;
};

const calculateGenerations = (people) => {
    if (people.length === 0) return 0;

    const getDepth = (person, visited = new Set()) => {
        if (visited.has(person.id)) return 0;
        visited.add(person.id);

        if (!person.parents || person.parents.length === 0) return 1;
        return 1 + Math.max(...person.parents.map((p) => getDepth(p, visited)));
    };

    return Math.max(...people.map((p) => getDepth(p)));
};

const filterPeopleBySearch = (people, query) => {
    if (!query.trim()) return people;

    const lowerQuery = query.toLowerCase();
    return people.filter((person) => {
        const fullName = (person.fullName || "").toLowerCase();
        const givenName = (person.givenName || "").toLowerCase();
        const surname = (person.surname || "").toLowerCase();
        const birthPlace = (person.birthData?.place || "").toLowerCase();

        return fullName.includes(lowerQuery) || givenName.includes(lowerQuery) || surname.includes(lowerQuery) || birthPlace.includes(lowerQuery);
    });
};

// Helper component: All People View with Sorting
const AllPeopleView = ({ people, searchQuery, setSearchQuery, setSelectedPerson }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const getSortedData = () => {
        const filtered = filterPeopleBySearch(people, searchQuery);
        if (!sortConfig.key) return filtered;

        const { key, direction } = sortConfig;
        return filtered.sort((a, b) => {
            let aVal = a[key];
            let bVal = b[key];

            // Handle nested properties like birthData.date
            if (key.includes(".")) {
                const keys = key.split(".");
                aVal = keys.reduce((obj, k) => obj?.[k], a);
                bVal = keys.reduce((obj, k) => obj?.[k], b);
            }

            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return direction === "asc" ? 1 : -1;
            if (bVal == null) return direction === "asc" ? -1 : 1;

            if (typeof aVal === "string" && typeof bVal === "string") {
                return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            if (typeof aVal === "number" && typeof bVal === "number") {
                return direction === "asc" ? aVal - bVal : bVal - aVal;
            }

            return 0;
        });
    };

    const handleSort = (key) => {
        if (sortConfig.key === key) {
            // Toggle off if clicking same column
            setSortConfig({ key: null, direction: "asc" });
        } else {
            // Turn on sorting for new column
            setSortConfig({ key, direction: "asc" });
        }
    };

    const renderSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) return "";
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    };

    return (
        <div style={{ marginTop: "1em" }}>
            <Form.Group className="mb-3">
                <Form.Control placeholder="Search by name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </Form.Group>
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("fullName")}>
                            Name{renderSortIndicator("fullName")}
                        </th>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("birthData.date")}>
                            Birth Date{renderSortIndicator("birthData.date")}
                        </th>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("birthData.place")}>
                            Birth Place{renderSortIndicator("birthData.place")}
                        </th>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("deathData.date")}>
                            Death Date{renderSortIndicator("deathData.date")}
                        </th>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("sex")}>
                            Sex{renderSortIndicator("sex")}
                        </th>
                        <th>Children</th>
                    </tr>
                </thead>
                <tbody>
                    {getSortedData().map((person) => (
                        <tr key={person.id} style={{ cursor: "pointer" }} onClick={() => setSelectedPerson(person)}>
                            <td>
                                <strong>{person.fullName || "Unknown"}</strong>
                            </td>
                            <td>{person.birthData?.date || "N/A"}</td>
                            <td>{person.birthData?.place || "N/A"}</td>
                            <td>{person.deathData?.date || "N/A"}</td>
                            <td>{person.sex || "N/A"}</td>
                            <td>{person.children?.length || 0}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

const FamilyTreeData = () => {
    const [loading, setLoading] = useState(true);
    const [people, setPeople] = useState([]);
    const [families, setFamilies] = useState({});
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedAncestors, setExpandedAncestors] = useState(new Set());
    const [ancestralFilter, setAncestralFilter] = useState("");

    useEffect(() => {
        const fetchAndParseGed = async () => {
            try {
                setLoading(true);
                const response = await fetch("/familytreedata/Fedorovsky_2025-12-14.ged");
                const gedContent = await response.text();
                const { individuals, families: parsedFamilies } = parseGedFile(gedContent);
                const familyTree = buildFamilyTree(individuals, parsedFamilies);

                setPeople(familyTree);
                setFamilies(parsedFamilies);
                setLoading(false);
            } catch (error) {
                console.error("Error parsing GED file:", error);
                setLoading(false);
            }
        };

        fetchAndParseGed();
    }, []);

    if (loading) return loader;

    // Calculate statistics
    const totalPeople = people.length;
    const totalGenerations = calculateGenerations(people);
    const totalFamilies = Object.keys(families).length;
    const peopleWithDates = people.filter((p) => p.birthData?.date).length;
    const peopleWithPhotos = people.filter((p) => p.photoId).length;

    // Get root ancestors
    const rootAncestors = getRootAncestors(people);

    return (
        <div style={containerStyle}>
            {/* <h1>Family Tree Data</h1> */}
            <Tabs defaultActiveKey="summary" id="familytree-tabs" className="mb-3">
                {/* Summary Tab */}
                <Tab eventKey="summary" title="Summary">
                    <div style={{ marginTop: "1em" }}>
                        <div className="row">
                            <div className="col-md-6 col-lg-4">
                                <Card style={statsCardStyle}>
                                    <Card.Body>
                                        <Card.Title>Total People</Card.Title>
                                        <div style={statsValueStyle}>{totalPeople}</div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <Card style={statsCardStyle}>
                                    <Card.Body>
                                        <Card.Title>Generations</Card.Title>
                                        <div style={statsValueStyle}>{totalGenerations}</div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <Card style={statsCardStyle}>
                                    <Card.Body>
                                        <Card.Title>Family Units</Card.Title>
                                        <div style={statsValueStyle}>{totalFamilies}</div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <Card style={statsCardStyle}>
                                    <Card.Body>
                                        <Card.Title>With Birth Dates</Card.Title>
                                        <div style={statsValueStyle}>{peopleWithDates}</div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <Card style={statsCardStyle}>
                                    <Card.Body>
                                        <Card.Title>With Photos</Card.Title>
                                        <div style={statsValueStyle}>{peopleWithPhotos}</div>
                                    </Card.Body>
                                </Card>
                            </div>
                            <div className="col-md-6 col-lg-4">
                                <Card style={statsCardStyle}>
                                    <Card.Body>
                                        <Card.Title>Root Ancestors</Card.Title>
                                        <div style={statsValueStyle}>{rootAncestors.length}</div>
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>
                    </div>
                </Tab>

                {/* All People Tab */}
                <Tab eventKey="people" title={`All People (${totalPeople})`}>
                    <AllPeopleView people={people} searchQuery={searchQuery} setSearchQuery={setSearchQuery} setSelectedPerson={setSelectedPerson} />
                </Tab>

                {/* Ancestral Lines Tab */}
                <Tab eventKey="ancestors" title="Ancestral Lines">
                    <div style={{ marginTop: "1em" }}>
                        <Form.Group className="mb-3">
                            <Form.Label>Filter ancestors:</Form.Label>
                            <Form.Control placeholder="Search ancestor name..." value={ancestralFilter} onChange={(e) => setAncestralFilter(e.target.value)} />
                        </Form.Group>
                        {rootAncestors.length > 0 ? (
                            rootAncestors.map((root) => (
                                <div key={root.id} style={{ marginBottom: "2em" }}>
                                    <h3>{root.fullName || "Unknown"}</h3>
                                    <AncestralLineView person={root} expandedIds={expandedAncestors} onToggleExpand={setExpandedAncestors} searchFilter={ancestralFilter} />
                                </div>
                            ))
                        ) : (
                            <p>No root ancestors found.</p>
                        )}
                    </div>
                </Tab>

                {/* Generational View Tab */}
                <Tab eventKey="generations" title="Generational View">
                    <div style={{ marginTop: "1em" }}>
                        <GenerationalView people={people} />
                    </div>
                </Tab>

                {/* Timeline Tab */}
                <Tab eventKey="timeline" title="Timeline">
                    <div style={{ marginTop: "1em" }}>
                        <TimelineView people={people} />
                    </div>
                </Tab>

                {/* Statistics Tab */}
                <Tab eventKey="statistics" title="Statistics">
                    <div style={{ marginTop: "1em" }}>
                        <StatisticsView people={people} families={families} />
                    </div>
                </Tab>

                {/* Name Distribution Tab */}
                <Tab eventKey="names" title="Surnames">
                    <div style={{ marginTop: "1em" }}>
                        <NameDistributionView people={people} />
                    </div>
                </Tab>

                {/* Birthplaces Tab */}
                <Tab eventKey="locations" title="Birthplaces">
                    <div style={{ marginTop: "1em" }}>
                        <BirthplacesView people={people} />
                    </div>
                </Tab>
            </Tabs>

            {/* Person Details Modal/Sidebar */}
            <Modal show={selectedPerson !== null} onHide={() => setSelectedPerson(null)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedPerson?.fullName || "Person Details"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{selectedPerson && <PersonDetailView person={selectedPerson} people={people} />}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedPerson(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

// Helper component: Ancestral Line View
// Helper function to count total descendants
const countDescendants = (person) => {
    if (!person.children || person.children.length === 0) return 0;
    return person.children.reduce((count, child) => count + 1 + countDescendants(child), 0);
};

const AncestralLineView = ({ person, level = 0, expandedIds = new Set(), onToggleExpand, searchFilter = "" }) => {
    const hasChildren = person.children && person.children.length > 0;
    const isExpanded = expandedIds.has(person.id);
    const margin = `${level * 1.5}em`;

    // Check if person matches search filter
    const matchesFilter = !searchFilter || (person.fullName || "").toLowerCase().includes(searchFilter.toLowerCase());

    // Sort children by descendant count (descending)
    const sortedChildren = hasChildren ? [...person.children].sort((a, b) => countDescendants(b) - countDescendants(a)) : [];

    return (
        <div style={{ marginLeft: margin, marginBottom: "0.5em" }}>
            <div
                style={{
                    padding: "0.5em",
                    backgroundColor: "inherit",
                    borderLeft: "2px solid #0056b3",
                    paddingLeft: "1em",
                    display: matchesFilter ? "block" : "none",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                    {hasChildren && (
                        <button
                            onClick={() => {
                                const newExpanded = new Set(expandedIds);
                                if (newExpanded.has(person.id)) {
                                    newExpanded.delete(person.id);
                                } else {
                                    newExpanded.add(person.id);
                                }
                                onToggleExpand(newExpanded);
                            }}
                            style={{
                                padding: "0.2em 0.4em",
                                fontSize: "0.8em",
                                cursor: "pointer",
                                border: "1px solid #999",
                                backgroundColor: "inherit",
                                color: "inherit",
                                borderRadius: "2px",
                            }}
                        >
                            {isExpanded ? "−" : "+"}
                        </button>
                    )}
                    <strong>{person.fullName || "Unknown"}</strong>
                    {person.birthData?.date && <span> ({person.birthData.date})</span>}
                    {hasChildren && <Badge bg="info">{person.children.length} children</Badge>}
                </div>
            </div>
            {hasChildren && isExpanded && (
                <div>
                    {sortedChildren.map((child) => (
                        <AncestralLineView key={child.id} person={child} level={level + 1} expandedIds={expandedIds} onToggleExpand={onToggleExpand} searchFilter={searchFilter} />
                    ))}
                </div>
            )}
        </div>
    );
};

// Helper component: Generational View
const GenerationalView = ({ people }) => {
    const generations = buildGenerations(people);

    return (
        <div>
            {generations.map((generation, index) => (
                <div key={index} style={{ marginBottom: "2em" }}>
                    <h3>Generation {index + 1}</h3>
                    <div className="row">
                        {generation.map((person) => (
                            <div key={person.id} className="col-md-6 col-lg-4">
                                <Card style={personCardStyle}>
                                    <Card.Body>
                                        <Card.Title>{person.fullName || "Unknown"}</Card.Title>
                                        {person.birthData?.date && (
                                            <div>
                                                <strong>Born:</strong> {person.birthData.date}
                                            </div>
                                        )}
                                        {person.birthData?.place && (
                                            <div>
                                                <strong>Place:</strong> {person.birthData.place}
                                            </div>
                                        )}
                                        {person.spouse && (
                                            <div>
                                                <strong>Spouse:</strong> {person.spouse.fullName}
                                            </div>
                                        )}
                                        <div>
                                            <strong>Children:</strong> {person.children?.length || 0}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

// Helper component: Timeline View
const TimelineView = ({ people }) => {
    const [selectedYear, setSelectedYear] = useState(null);

    // Helper function to extract year - more robust version
    const getYearFromDate = (dateString) => {
        if (!dateString) return null;
        // Extract 4-digit year from any position in the string
        const match = dateString.match(/\b(1\d{3}|2\d{3})\b/);
        return match ? parseInt(match[0], 10) : null;
    };

    // Collect all events: births, deaths, and other life events
    const allEvents = [];
    people.forEach((person) => {
        if (person.birthData?.date) {
            const year = getYearFromDate(person.birthData.date);
            if (year) {
                allEvents.push({
                    year,
                    type: "birth",
                    person: person.fullName,
                    date: person.birthData.date,
                    place: person.birthData.place,
                    id: `${person.id}-birth`,
                });
            }
        }
        if (person.deathData?.date) {
            const year = getYearFromDate(person.deathData.date);
            if (year) {
                allEvents.push({
                    year,
                    type: "death",
                    person: person.fullName,
                    date: person.deathData.date,
                    place: person.deathData.place,
                    id: `${person.id}-death`,
                });
            }
        }
    });

    // Sort all events by year, then by type (births first)
    allEvents.sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.type === "birth" ? -1 : 1;
    });

    // Get min and max years
    const validYears = allEvents.map((e) => e.year).filter((y) => y && y < 2500);
    const minYear = validYears.length > 0 ? Math.min(...validYears) : new Date().getFullYear();
    const maxYear = validYears.length > 0 ? Math.max(...validYears) : minYear + 1;

    // Initialize selectedYear to minYear if not set
    const currentSelectedYear = selectedYear !== null ? selectedYear : minYear;

    // Filter events for selected year
    const eventsForYear = allEvents.filter((e) => e.year === currentSelectedYear);

    // Auto-scroll to first event of selected year when year changes
    const getEventIcon = (type) => {
        if (type === "birth") return "👶";
        if (type === "death") return "⚰️";
        return "📌";
    };

    const handleYearInputChange = (e) => {
        const inputValue = e.target.value;
        if (inputValue === "") return;
        const year = Number(inputValue);
        if (!Number.isNaN(year) && year >= minYear && year <= maxYear) {
            setSelectedYear(year);
        }
    };

    return (
        <div>
            <h3>Family Timeline</h3>
            <div style={{ marginBottom: "2em" }}>
                {/* Year slider with better styling */}
                <div className="timeline-slider-section" style={{ marginBottom: "2em", padding: "1.5em", backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
                    <div style={{ marginBottom: "1.5em" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1em", flexWrap: "wrap", gap: "1em" }}>
                            <label className="timeline-year-label" style={{ fontWeight: "bold", fontSize: "1.1em" }}>
                                Select Year: <span style={{ color: "#0056b3", fontSize: "1.3em" }}>{currentSelectedYear}</span>
                            </label>
                            <input
                                type="number"
                                className="timeline-year-input"
                                min={minYear}
                                max={maxYear}
                                value={currentSelectedYear}
                                onChange={handleYearInputChange}
                                style={{
                                    width: "100px",
                                    padding: "0.5em",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontSize: "1em",
                                    textAlign: "center",
                                }}
                            />
                            <span style={{ color: "#666", fontSize: "0.9em" }}>
                                ({minYear} - {maxYear})
                            </span>
                        </div>
                        <input
                            type="range"
                            min={minYear}
                            max={maxYear}
                            value={currentSelectedYear}
                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                            style={{
                                width: "100%",
                                height: "6px",
                                borderRadius: "3px",
                                background: `linear-gradient(to right, #0056b3 0%, #0056b3 ${((currentSelectedYear - minYear) / (maxYear - minYear)) * 100}%, #e0e0e0 ${((currentSelectedYear - minYear) / (maxYear - minYear)) * 100}%, #e0e0e0 100%)`,
                                outline: "none",
                                WebkitAppearance: "none",
                                cursor: "pointer",
                            }}
                        />
                    </div>

                    {/* Events count summary */}
                    <div className="timeline-events-section" style={{ marginTop: "1em", padding: "0.8em", backgroundColor: "white", borderRadius: "4px", textAlign: "center", color: "#666" }}>
                        <span>{eventsForYear.length > 0 ? `${eventsForYear.length} event${eventsForYear.length !== 1 ? "s" : ""} in ${currentSelectedYear}` : `No events recorded in ${currentSelectedYear}`}</span>
                    </div>
                </div>

                {/* Events for selected year */}
                {eventsForYear.length > 0 && (
                    <div className="timeline-selected-events" style={{ backgroundColor: "#f0f8ff", padding: "1.5em", borderRadius: "8px", marginTop: "1.5em", borderLeft: "4px solid #0056b3" }}>
                        <h5 style={{ marginBottom: "1.2em", color: "#0056b3" }}>Events in {currentSelectedYear}</h5>
                        <div className="timeline" style={{ position: "relative", paddingLeft: "2em" }}>
                            {eventsForYear.map((event) => (
                                <div
                                    key={event.id}
                                    style={{
                                        marginBottom: "1.2em",
                                        borderLeft: event.type === "birth" ? "3px solid #28a745" : "3px solid #dc3545",
                                        paddingLeft: "1em",
                                        paddingBottom: "0.5em",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", marginBottom: "0.3em" }}>
                                        <span style={{ fontSize: "1.5em", marginRight: "0.7em" }}>{getEventIcon(event.type)}</span>
                                        <strong style={{ fontSize: "1.05em", color: "inherit" }}>{event.person}</strong>
                                        <span style={{ marginLeft: "0.7em", color: event.type === "birth" ? "#28a745" : "#dc3545", fontWeight: "600" }}>{event.type === "birth" ? "Born" : "Died"}</span>
                                    </div>
                                    <small style={{ color: "inherit", opacity: 0.8, display: "block" }}>{event.date}</small>
                                    {event.place && <small style={{ color: "inherit", opacity: 0.7 }}>📍 {event.place}</small>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Full timeline list */}
                <div className="timeline-events-section" style={{ marginTop: "2em" }}>
                    <h5 style={{ marginBottom: "1em" }}>
                        All Events ({allEvents.length} total) <small style={{ color: "#999" }}>Click to jump to year</small>
                    </h5>
                    <div className="timeline" style={{ position: "relative", paddingLeft: "2em", maxHeight: "600px", overflowY: "auto" }}>
                        {allEvents.map((event) => (
                            <div
                                key={event.id}
                                className="timeline-event-item"
                                data-year={event.year}
                                data-selected={currentSelectedYear === event.year}
                                onClick={() => setSelectedYear(event.year)}
                                style={{
                                    marginBottom: "0.6em",
                                    borderLeft: event.type === "birth" ? "2px solid #28a745" : "2px solid #dc3545",
                                    paddingLeft: "1em",
                                    padding: "0.6em 0.6em 0.6em 1em",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                    transition: "all 0.2s",
                                }}
                            >
                                <div style={{ fontSize: "0.9em", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.6em" }}>
                                        <span>{getEventIcon(event.type)}</span>
                                        <strong style={{ minWidth: "40px" }}>{event.year}:</strong>
                                        <span>{event.person}</span>
                                        <span style={{ color: event.type === "birth" ? "#28a745" : "#dc3545", fontSize: "0.85em" }}>({event.type})</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper component: Statistics View
const StatisticsView = ({ people, families }) => {
    const avgChildren = people.length > 0 ? (people.reduce((sum, p) => sum + (p.children?.length || 0), 0) / people.length).toFixed(2) : 0;
    const avgAge = calculateAverageAge(people);
    const maleCount = people.filter((p) => p.sex === "M").length;
    const femaleCount = people.filter((p) => p.sex === "F").length;
    const malePercent = ((maleCount / people.length) * 100).toFixed(1);
    const femalePercent = ((femaleCount / people.length) * 100).toFixed(1);

    return (
        <div>
            <div className="row">
                <div className="col-md-6">
                    <Card style={statsCardStyle}>
                        <Card.Body>
                            <Card.Title>Average Children per Person</Card.Title>
                            <div style={statsValueStyle}>{avgChildren}</div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-6">
                    <Card style={statsCardStyle}>
                        <Card.Body>
                            <Card.Title>Average Age at Record</Card.Title>
                            <div style={statsValueStyle}>{avgAge}</div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-6">
                    <Card style={statsCardStyle}>
                        <Card.Body>
                            <Card.Title>Gender Distribution</Card.Title>
                            <div>
                                <Badge bg="primary">
                                    Male: {malePercent}% ({maleCount})
                                </Badge>
                            </div>
                            <div>
                                <Badge bg="danger">
                                    Female: {femalePercent}% ({femaleCount})
                                </Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <div className="col-md-6">
                    <Card style={statsCardStyle}>
                        <Card.Body>
                            <Card.Title>Documentation</Card.Title>
                            <div>
                                <div>Birth Dates: {people.filter((p) => p.birthData?.date).length}</div>
                                <div>Death Dates: {people.filter((p) => p.deathData?.date).length}</div>
                                <div>Photos: {people.filter((p) => p.photoId).length}</div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

// Helper component: Person Detail View
const PersonDetailView = ({ person, people }) => {
    const parents = person.parents || [];
    const children = person.children || [];
    const ancestors = getAncestors(person);
    const descendants = getDescendants(person);

    return (
        <div>
            <Card>
                <Card.Body>
                    <h2>{person.fullName || "Unknown"}</h2>
                    <div className="row">
                        <div className="col-md-6">
                            <h4>Personal Information</h4>
                            <p>
                                <strong>ID:</strong> {person.id}
                            </p>
                            <p>
                                <strong>Sex:</strong> {getSexDisplay(person.sex)}
                            </p>
                            {person.birthData && (
                                <div>
                                    <p>
                                        <strong>Birth Date:</strong> {person.birthData.date}
                                    </p>
                                    {person.birthData.place && (
                                        <p>
                                            <strong>Birth Place:</strong> {person.birthData.place}
                                        </p>
                                    )}
                                </div>
                            )}
                            {person.deathData && (
                                <div>
                                    <p>
                                        <strong>Death Date:</strong> {person.deathData.date}
                                    </p>
                                    {person.deathData.place && (
                                        <p>
                                            <strong>Death Place:</strong> {person.deathData.place}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <h4>Family Information</h4>
                            {person.spouse && (
                                <p>
                                    <strong>Spouse:</strong> {person.spouse.fullName}
                                </p>
                            )}
                            <p>
                                <strong>Parents:</strong> {parents.length > 0 ? parents.map((p) => p.fullName).join(", ") : "None listed"}
                            </p>
                            <p>
                                <strong>Children:</strong> {children.length > 0 ? children.length : "None listed"}
                            </p>
                            <p>
                                <strong>Total Ancestors:</strong> {ancestors.size}
                            </p>
                            <p>
                                <strong>Total Descendants:</strong> {descendants.size}
                            </p>
                        </div>
                    </div>
                    {children.length > 0 && (
                        <div style={{ marginTop: "1em" }}>
                            <h4>Children</h4>
                            <ul>
                                {children.map((child) => (
                                    <li key={child.id}>
                                        {child.fullName} {child.birthData?.date && `(${child.birthData.date})`}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

// Helper component: Name Distribution View
const NameDistributionView = ({ people }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const surnames = {};

    people.forEach((person) => {
        const surname = person.surname || "Unknown";
        surnames[surname] = (surnames[surname] || 0) + 1;
    });

    const surnameData = Object.entries(surnames).map(([surname, count]) => ({ surname, count }));

    // Apply sorting if enabled
    if (sortConfig.key) {
        surnameData.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (typeof aVal === "string") {
                return sortConfig.direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        });
    } else {
        // Default: sort by count descending
        surnameData.sort((a, b) => b.count - a.count);
    }

    const handleSort = (key) => {
        if (sortConfig.key === key) {
            setSortConfig({ key: null, direction: "asc" });
        } else {
            setSortConfig({ key, direction: "asc" });
        }
    };

    const renderSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) return "";
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    };

    return (
        <div>
            <h3>Surname Distribution</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("surname")}>
                            Surname{renderSortIndicator("surname")}
                        </th>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("count")}>
                            Count{renderSortIndicator("count")}
                        </th>
                        <th>Percentage</th>
                    </tr>
                </thead>
                <tbody>
                    {surnameData.map(({ surname, count }) => (
                        <tr key={surname}>
                            <td>{surname}</td>
                            <td>{count}</td>
                            <td>{((count / people.length) * 100).toFixed(1)}%</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

// Helper component: Birthplaces View
const BirthplacesView = ({ people }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const locations = {};

    people.forEach((person) => {
        const place = person.birthData?.place || "Unknown";
        locations[place] = (locations[place] || 0) + 1;
    });

    const locationData = Object.entries(locations).map(([location, count]) => ({ location, count }));

    // Apply sorting if enabled
    if (sortConfig.key) {
        locationData.sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            if (typeof aVal === "string") {
                return sortConfig.direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        });
    } else {
        // Default: sort by count descending
        locationData.sort((a, b) => b.count - a.count);
    }

    const handleSort = (key) => {
        if (sortConfig.key === key) {
            setSortConfig({ key: null, direction: "asc" });
        } else {
            setSortConfig({ key, direction: "asc" });
        }
    };

    const renderSortIndicator = (columnKey) => {
        if (sortConfig.key !== columnKey) return "";
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    };

    return (
        <div>
            <h3>Birth Place Distribution</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("location")}>
                            Location{renderSortIndicator("location")}
                        </th>
                        <th style={{ cursor: "pointer", userSelect: "none" }} onClick={() => handleSort("count")}>
                            Count{renderSortIndicator("count")}
                        </th>
                        <th>Percentage</th>
                    </tr>
                </thead>
                <tbody>
                    {locationData.map(({ location, count }) => (
                        <tr key={location}>
                            <td>{location}</td>
                            <td>{count}</td>
                            <td>{((count / people.length) * 100).toFixed(1)}%</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

export default FamilyTreeData;
